import React from 'react';

const BMI_CATEGORIES = [
  { max: 18.5, label: 'Underweight', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  { max: 25,   label: 'Healthy',     color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  { max: 30,   label: 'Overweight',  color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  { max: Infinity, label: 'Obese',   color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
];

function getBmiCat(bmi) {
  return BMI_CATEGORIES.find(c => bmi < c.max) || BMI_CATEGORIES[BMI_CATEGORIES.length - 1];
}

export default function HealthStats({ user }) {
  if (!user) return null;

  const cat = getBmiCat(user.bmi);

  // BMI ring progress (0-40 range mapped to 0-100%)
  const bmiPct = Math.min(Math.max((user.bmi / 40) * 100, 0), 100);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 16,
    }}>
      {/* BMI Card */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        padding: '24px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(241,245,249,0.4)', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
          Body Mass Index
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div style={{
            fontSize: '2.8rem', fontWeight: 800,
            color: cat.color,
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '-0.04em',
            lineHeight: 1,
          }}>
            {user.bmi}
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '4px 10px', borderRadius: 99,
            background: cat.bg,
            color: cat.color,
            fontSize: 12, fontWeight: 600,
            fontFamily: 'Inter, sans-serif',
            marginBottom: 4,
          }}>
            {cat.label}
          </div>
        </div>

        {/* Bar */}
        <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${bmiPct}%`,
            background: `linear-gradient(90deg, #4ade80, ${cat.color})`,
            borderRadius: 99,
            transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
          }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(241,245,249,0.3)', fontFamily: 'Inter, sans-serif' }}>
          <span>Underweight</span><span>Normal</span><span>Obese</span>
        </div>
      </div>

      {/* Calorie Goal Card */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18,
        padding: '24px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(241,245,249,0.4)', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>
          Daily Calorie Goal
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <div style={{
            fontSize: '2.8rem', fontWeight: 800,
            color: '#a3e635',
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '-0.04em',
            lineHeight: 1,
          }}>
            {user.dailyCalorieGoal?.toLocaleString()}
          </div>
          <span style={{ fontSize: 14, color: 'rgba(241,245,249,0.4)', marginBottom: 6, fontFamily: 'Inter, sans-serif' }}>
            kcal / day
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <MacroBar label="Protein" pct={25} color="#818cf8" />
          <MacroBar label="Carbs" pct={50} color="#a3e635" />
          <MacroBar label="Fat" pct={25} color="#fb923c" />
        </div>
      </div>
    </div>
  );
}

function MacroBar({ label, pct, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ fontSize: 11, color: 'rgba(241,245,249,0.4)', fontFamily: 'Inter, sans-serif', width: 42, flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: color, borderRadius: 99,
        }} />
      </div>
      <div style={{ fontSize: 11, color: 'rgba(241,245,249,0.3)', fontFamily: 'Inter, sans-serif', width: 28, textAlign: 'right', flexShrink: 0 }}>
        {pct}%
      </div>
    </div>
  );
}
